import React from 'react';
import { renderMarkdown } from '../../utils/markdown';
import * as api from '../../api';

const MAX_POST_CHARS = 3000;

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

function base64ToArrayBuffer(value) {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

async function compressImage(file, maxSide = 1280, quality = 0.76) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(result => result ? resolve(result) : reject(new Error('Image compression failed.')), 'image/webp', quality);
  });
  bitmap.close?.();
  return blob;
}

async function encryptImageBlob(blob, name) {
  const keyBytes = window.crypto.getRandomValues(new Uint8Array(32));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await window.crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['encrypt']);
  const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, await blob.arrayBuffer());
  return {
    type: 'encrypted-image',
    algorithm: 'AES-256-GCM',
    name,
    mime: 'image/webp',
    size: blob.size,
    iv: arrayBufferToBase64(iv.buffer),
    key: arrayBufferToBase64(keyBytes.buffer),
    ciphertext: arrayBufferToBase64(ciphertext)
  };
}

function useDecryptedImage(attachment) {
  const [url, setUrl] = React.useState('');

  React.useEffect(() => {
    let revoked = '';
    let mounted = true;
    async function decrypt() {
      if (!attachment?.ciphertext || !attachment?.key || !attachment?.iv) {
        setUrl('');
        return;
      }
      try {
        const keyBytes = base64ToArrayBuffer(attachment.key);
        const iv = new Uint8Array(base64ToArrayBuffer(attachment.iv));
        const ciphertext = base64ToArrayBuffer(attachment.ciphertext);
        const cryptoKey = await window.crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt']);
        const plain = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ciphertext);
        const objectUrl = URL.createObjectURL(new Blob([plain], { type: attachment.mime || 'image/webp' }));
        revoked = objectUrl;
        if (mounted) {
          setUrl(objectUrl);
        }
      } catch (_) {
        if (mounted) {
          setUrl('');
        }
      }
    }
    decrypt();
    return () => {
      mounted = false;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [attachment]);

  return url;
}

function ChannelAvatar({ avatar, name }) {
  const avatarObject = avatar && typeof avatar === 'object' ? avatar : null;
  const url = useDecryptedImage(avatarObject?.type === 'encrypted-image' ? avatarObject : null);
  if (url) {
    return <img src={url} alt="" />;
  }
  return <span>{String(name || 'C').slice(0, 1).toUpperCase()}</span>;
}

function EncryptedImage({ attachment }) {
  const url = useDecryptedImage(attachment);
  if (!url) {
    return <div className="public-channel-image-placeholder">Encrypted image preview unavailable</div>;
  }
  return <img className="public-channel-post-image" src={url} alt={attachment.name || 'Channel attachment'} />;
}

function formatChannelDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function PublicChannelsPane({
  activeIdentity,
  activePublicChannel,
  creatorOpen,
  setCreatorOpen,
  publicChannelPosts,
  publicChannelsError,
  publicChannelPostsLoading,
  createChannel,
  updateChannel,
  toggleSubscription,
  createPost,
  reportChannel,
  deleteChannel,
  verifyChannel,
  showConfirm,
  triggerAlert,
  setMobileMenuOpen
}) {
  const [mode, setMode] = React.useState('view');
  const [nameInput, setNameInput] = React.useState('');
  const [descriptionInput, setDescriptionInput] = React.useState('');
  const [avatarEnvelope, setAvatarEnvelope] = React.useState(null);
  const [postBody, setPostBody] = React.useState('');
  const [postAttachments, setPostAttachments] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const postRef = React.useRef(null);

  // Reporting states
  const [reportModalOpen, setReportModalOpen] = React.useState(false);
  const [reportCategory, setReportCategory] = React.useState('spam');
  const [reportSeverity, setReportSeverity] = React.useState('low');
  const [reportComment, setReportComment] = React.useState('');

  const submitReportHandler = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await reportChannel({
        channelId: activePublicChannel.id,
        category: reportCategory,
        severity: reportSeverity,
        comment: reportComment
      });
      setReportModalOpen(false);
      setReportComment('');
    } catch (err) {
      setError(err.message || 'Meldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const [roles, setRoles] = React.useState([]);
  React.useEffect(() => {
    async function loadRoles() {
      try {
        const res = await api.getGovernanceRoles();
        if (res && res.roles) {
          setRoles(res.roles);
        }
      } catch (_) {}
    }
    if (activeIdentity) {
      loadRoles();
    }
  }, [activeIdentity]);

  const isOperator = roles.includes('node_operator');

  const handleToggleVerification = async () => {
    if (!activeIdentity || !activePublicChannel || !verifyChannel) return;
    setBusy(true);
    setError('');
    const targetVal = !activePublicChannel.isVerified;
    try {
      await verifyChannel(activePublicChannel.id, targetVal);
    } catch (err) {
      setError(err.message || 'Aktion fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteChannelClick = () => {
    if (!activePublicChannel || !showConfirm) return;
    showConfirm(
      'Kanal löschen',
      'Möchtest du diesen Kanal wirklich dauerhaft löschen? Dies kann nicht rückgängig gemacht werden und alle Beiträge werden gelöscht.',
      async () => {
        setBusy(true);
        setError('');
        try {
          await deleteChannel(activePublicChannel.id);
        } catch (err) {
          setError(err.message || 'Löschen fehlgeschlagen.');
        } finally {
          setBusy(false);
        }
      },
      null,
      'Kanal löschen',
      'Abbrechen',
      true
    );
  };

  React.useEffect(() => {
    if (activePublicChannel) {
      setMode('view');
      setNameInput(activePublicChannel.name || '');
      setDescriptionInput(activePublicChannel.description || '');
      setAvatarEnvelope(activePublicChannel.avatar || null);
    } else if (creatorOpen) {
      setMode('create');
      setNameInput('');
      setDescriptionInput('');
      setAvatarEnvelope(null);
    } else {
      setMode('view');
      setNameInput('');
      setDescriptionInput('');
      setAvatarEnvelope(null);
    }
    setError('');
    setPostBody('');
    setPostAttachments([]);
  }, [activePublicChannel, creatorOpen]);

  const wrapSelection = (before, after = before) => {
    const input = postRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selected = postBody.slice(start, end) || 'text';
    const next = `${postBody.slice(0, start)}${before}${selected}${after}${postBody.slice(end)}`;
    setPostBody(next.slice(0, MAX_POST_CHARS));
    window.requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const handleAvatarFile = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const compressed = await compressImage(file, 640, 0.72);
      const encrypted = await encryptImageBlob(compressed, file.name);
      setAvatarEnvelope(encrypted);
    } catch (err) {
      setError(err.message || 'Avatar image could not be processed.');
    } finally {
      event.target.value = '';
    }
  };

  const handlePostImage = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const compressed = await compressImage(file);
      const encrypted = await encryptImageBlob(compressed, file.name);
      setPostAttachments(prev => [encrypted, ...prev].slice(0, 1));
    } catch (err) {
      setError(err.message || 'Image could not be processed.');
    } finally {
      event.target.value = '';
    }
  };

  const submitChannel = async event => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'edit' && activePublicChannel) {
        await updateChannel({
          channelId: activePublicChannel.id,
          name: nameInput,
          description: descriptionInput,
          avatar: avatarEnvelope
        });
        setMode('view');
        if (setCreatorOpen) setCreatorOpen(false);
      } else {
        await createChannel({
          name: nameInput,
          description: descriptionInput,
          avatar: avatarEnvelope
        });
        if (setCreatorOpen) setCreatorOpen(false);
      }
    } catch (err) {
      setError(err.message || 'Channel could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const submitPost = async event => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await createPost({
        body: postBody,
        attachments: postAttachments.length > 0 ? postAttachments : null
      });
      setPostBody('');
      setPostAttachments([]);
    } catch (err) {
      setError(err.message || 'Post could not be published.');
    } finally {
      setBusy(false);
    }
  };

  if (!activeIdentity) {
    return (
      <div className="public-channel-pane empty is-empty-state">
        <button type="button" className="mobile-floating-menu mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>Menu</button>
        <div className="public-channel-empty-icon">ID</div>
        <h2>Channels</h2>
        <p>Create an identity first to view, subscribe, and publish channels.</p>
      </div>
    );
  }

  const showForm = mode === 'create' || mode === 'edit';

  return (
    <div className={`public-channel-pane ${activePublicChannel ? 'has-active-channel' : 'is-empty-state'}`}>
      <div className="detail-mobile-actions">
        <button type="button" className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>Menu</button>
      </div>

      {publicChannelsError && (
        <div className="public-channel-route-warning">
          <strong>Channels backend route unavailable</strong>
          <span>{publicChannelsError}. The deployed backend needs the new public channel routes before channels can be used on this server.</span>
        </div>
      )}

      {showForm ? (
        <section className="public-channel-editor">
          <h2>{mode === 'edit' ? 'Edit Channel' : 'Create Channel'}</h2>
          <form onSubmit={submitChannel}>
            <div className="public-channel-avatar-edit">
              <div className="public-channel-avatar">
                <ChannelAvatar avatar={avatarEnvelope} name={nameInput} />
              </div>
              <label className="btn-secondary public-channel-file-btn">
                Set encrypted image
                <input type="file" accept="image/*" onChange={handleAvatarFile} />
              </label>
            </div>
             <div className="form-group">
               <label>Channel name (Handle)</label>
               <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                 <span style={{ position: 'absolute', left: '12px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>@</span>
                 <input 
                   className="input-field" 
                   value={nameInput.startsWith('@') ? nameInput.slice(1) : nameInput} 
                   maxLength={80} 
                   onChange={event => {
                     const val = event.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                     setNameInput('@' + val.toLowerCase());
                   }} 
                   placeholder="channelhandle"
                   style={{ paddingLeft: '28px', width: '100%', boxSizing: 'border-box' }}
                   required 
                 />
               </div>
             </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="input-field" value={descriptionInput} maxLength={600} onChange={event => setDescriptionInput(event.target.value)} />
            </div>
            {error && <p className="form-error">{error}</p>}
            <div className="public-channel-actions">
              <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving...' : 'Save Channel'}</button>
              <button type="button" className="btn-secondary" onClick={() => {
                if (setCreatorOpen) setCreatorOpen(false);
                setMode('view');
              }}>Cancel</button>
            </div>
          </form>
        </section>
      ) : activePublicChannel ? (
        <>
          <header className="public-channel-header">
            <div className="public-channel-avatar">
              <ChannelAvatar avatar={activePublicChannel.avatar} name={activePublicChannel.name} />
            </div>
            <div className="public-channel-header-copy">
              <div className="public-channel-eyebrow">
                <span>Public Channel</span>
                <span>{activePublicChannel.isAdmin ? 'Admin Console' : 'Viewer Mode'}</span>
              </div>
              <h2>
                {activePublicChannel.name}
                {activePublicChannel.isVerified && (
                  <span style={{ 
                    color: '#00f2fe', 
                    background: 'rgba(0, 242, 254, 0.1)', 
                    border: '1px solid rgba(0, 242, 254, 0.3)', 
                    borderRadius: '4px', 
                    padding: '2px 6px', 
                    fontSize: '0.65rem', 
                    textTransform: 'uppercase', 
                    fontFamily: 'var(--font-mono)', 
                    marginLeft: '8px', 
                    verticalAlign: 'middle',
                    display: 'inline-block'
                  }}>
                    ✓ Zertifiziert
                  </span>
                )}
              </h2>
              <p>{activePublicChannel.description || 'No description yet.'}</p>
              <div className="public-channel-meta-row">
                <span>{activePublicChannel.subscriberCount || 0} subscribers</span>
                <span>Encrypted media</span>
                <span>Federation ready</span>
              </div>
            </div>
            <div className="public-channel-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button type="button" className="btn-secondary" onClick={() => toggleSubscription(activePublicChannel)}>
                {activePublicChannel.isSubscribed ? 'Abmelden' : 'Abonnieren'}
              </button>
              {activePublicChannel.isAdmin && (
                <>
                  <button type="button" className="btn-action" onClick={() => setMode('edit')}>Bearbeiten</button>
                  <button type="button" className="btn-danger-outline" onClick={handleDeleteChannelClick}>Kanal löschen</button>
                </>
              )}
              {isOperator && (
                <button type="button" className="btn-action" style={{ border: '1px solid var(--accent-cyan)', background: 'transparent', color: 'var(--accent-cyan)' }} onClick={handleToggleVerification}>
                  {activePublicChannel.isVerified ? 'Entzertifizieren' : 'Kanal Zertifizieren'}
                </button>
              )}
              {!activePublicChannel.isAdmin && (
                <button type="button" className="btn-danger-outline" onClick={() => setReportModalOpen(true)}>Kanal Melden</button>
              )}
            </div>
          </header>

          {activePublicChannel.isSuspended && (
            <div className="public-channel-suspended-banner">
              <strong>⚠️ Kanal Gesperrt</strong>
              <p>Dieser Kanal wurde durch den Abuse-Consensus wegen Richtlinienverstößen gesperrt. Begründung: {activePublicChannel.suspensionReason || 'Richtlinienverletzung'}</p>
            </div>
          )}

          <section className="public-channel-posts gaia-scrollbar">
            {publicChannelPostsLoading ? (
              <div className="public-channel-empty">Lade Beiträge...</div>
            ) : publicChannelPosts.length === 0 ? (
              <div className="public-channel-empty">Keine Beiträge vorhanden.</div>
            ) : publicChannelPosts.map(post => {
              const attachments = Array.isArray(post.attachments) ? post.attachments : [];
              return (
                <article className="public-channel-post" key={post.id}>
                  <div className="public-channel-post-top">
                    <span>{activePublicChannel.name}</span>
                    <time>{formatChannelDate(post.createdAt)}</time>
                  </div>
                  {attachments.map((attachment, index) => (
                    attachment?.type === 'encrypted-image'
                      ? <EncryptedImage key={`${post.id}-${index}`} attachment={attachment} />
                      : null
                  ))}
                  {post.body && <div className="public-channel-post-body">{renderMarkdown(post.body)}</div>}
                </article>
              );
            })}
          </section>

          {activePublicChannel.isAdmin && !activePublicChannel.isSuspended && (
            <form className="public-channel-composer" onSubmit={submitPost}>
              <div className="public-channel-toolbar">
                <button type="button" title="Fett" onClick={() => wrapSelection('**')}>B</button>
                <button type="button" title="Kursiv" onClick={() => wrapSelection('*')}>I</button>
                <button type="button" title="Unterstrichen" onClick={() => wrapSelection('__')}>U</button>
                <label className="public-channel-composer-img-label">
                  IMG
                  <input type="file" accept="image/*" onChange={handlePostImage} />
                </label>
              </div>
              <textarea
                ref={postRef}
                className="input-field"
                value={postBody}
                maxLength={MAX_POST_CHARS}
                onChange={event => setPostBody(event.target.value)}
                placeholder="Teile ein verschlüsseltes Update..."
              />
              <div className="public-channel-composer-footer">
                <span>{postBody.length}/{MAX_POST_CHARS}</span>
                {postAttachments.length > 0 && <span>Bild angehängt (verschlüsselt)</span>}
                <button type="submit" className="btn-primary" disabled={busy || (!postBody.trim() && postAttachments.length === 0)}>
                  {busy ? 'Veröffentliche...' : 'Veröffentlichen'}
                </button>
              </div>
              {error && <p className="form-error">{error}</p>}
            </form>
          )}
        </>
      ) : (
        <section className="public-channel-editor">
          <div className="public-channel-empty-icon">CH</div>
          <h2>Kanäle</h2>
          <p className="public-channel-viewer-note">Wähle einen Kanal aus der Liste oder erstelle einen neuen Kanal.</p>
          <button type="button" className="btn-primary" onClick={() => {
            if (setCreatorOpen) setCreatorOpen(true);
            setMode('create');
          }}>Kanal Erstellen</button>
        </section>
      )}

      {/* Abuse report modal overlay */}
      {reportModalOpen && (
        <div className="popup-overlay abuse-report-overlay">
          <div className="popup-card glass-panel" style={{ maxWidth: '480px', width: '100%', padding: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Kanal melden: {activePublicChannel.name}</h3>
            <form onSubmit={submitReportHandler}>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Kategorie des Verstoßes</label>
                <select 
                  className="input-field" 
                  value={reportCategory} 
                  onChange={e => setReportCategory(e.target.value)}
                  style={{ width: '100%', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                >
                  <option value="spam">Spam / Abuse</option>
                  <option value="phishing">Betrug / Phishing</option>
                  <option value="malware">Malware / Technische Angriffe</option>
                  <option value="harassment">Belästigung / Bedrohung</option>
                  <option value="illegal_content">Illegale Inhalte (Drogen, Gewalt, etc.)</option>
                  <option value="threat">Akute Bedrohung / Gefahr für Leib und Leben</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label>Dringlichkeit / Dringlichkeitsstufe</label>
                <select 
                  className="input-field" 
                  value={reportSeverity} 
                  onChange={e => setReportSeverity(e.target.value)}
                  style={{ width: '100%', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                >
                  <option value="low">Niedrig (Spam)</option>
                  <option value="medium">Mittel</option>
                  <option value="high">Hoch (Illegale Inhalte, Bedrohung)</option>
                  <option value="critical">Kritisch (Lebensgefahr)</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Zusätzliche Anmerkungen oder Nachweise</label>
                <textarea 
                  className="input-field" 
                  value={reportComment} 
                  placeholder="Geben Sie details oder den kryptografischen Nachweis des Verstoßes an..."
                  onChange={e => setReportComment(e.target.value)}
                  style={{ width: '100%', height: '80px' }}
                />
              </div>

              <div className="button-row" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setReportModalOpen(false)}>Abbrechen</button>
                <button type="submit" className="btn-danger" disabled={busy}>
                  {busy ? 'Wird gemeldet...' : 'Meldung abschicken'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
